import { resolveUrl } from "~/utils/url/urlResolver";
import { type ConfiguratorType } from "~/domains/configurator/types/ConfiguratorType";

import { isNonNull } from "~/common/utils/reducers";

export const INPUT_VALIDATION_DELAY = 2000;

export const CSTIC_GUIDEWAY_AMOUNT_NAME = "VSANZ_066";
export const GET_CARRIAGE_AMOUNT_CSTIC_NAME = (guidewayIndex: number) => `V_S${guidewayIndex}_AE_066`;
export const CSTIC_SIZE_NAME = "VBGR_066";
export const CSTIC_DESIGN_TYPE_NAME = "VH_MKB_066";
export const CSTIC_VHOE = "VHOE_066";
export const CSTIC_VHOE_DEFAULT_VALUE = "";
export const CSTIC_VHOE_STANDARD_VALUE = "/";
export const CSTIC_VALUE_DESIGN_TYPE_KUVE = "KUVEW";
export const CSTIC_VALUE_VHOE_W = "W";

export const CSTIC_VALUE_WILDCARD_IS_SET = -1;
export const CSTIC_VALUE_WILDCARD_IS_NOT_SET = -2;

export const GET_RAIL_LOCATING_CSTIC_NAME = (index: number) => `V_S${index}_ANS_066`;
export const GET_CARRIAGE_LOCATING_CSTIC_NAME = (index: number) => `V_S${index}_ANW_066`;

export const LINEAR_MIN_STEP_TO_BUY = 3; // counted from 0, so 4th step
export const LINEAR_MIN_STEP_TO_SAVE = 3; // counted from 0, so 4th step

// Guideway Cstics
export const CSTIC_LENGTH_NAME = "VKWL_1D_064";
export const CSTIC_ENABLE_ALAR_NAME = "VKVA_064";
export const CSTIC_ENABLE_ALAR_VALUE_DISABLE = "N";
export const CSTIC_ENABLE_ALAR_VALUE_ENABLE = "J";
export const CSTIC_AL_NAME = "VAAL_064";
export const CSTIC_AR_NAME = "VAAR_064";
export const CSTIC_LABEL_SUPPORT_NAME = "VBUCH_064"; // This cstic is used only for label and info in step 6

export const CSTIC_VAAL_MIN_NAME = "VAL_IB_064";
export const CSTIC_VAAL_MAX_NAME = "VAL_AB_064";
export const CSTIC_GUIDEWAY_LOCATING_FACE = "VANS_064";

export const CSTIC_PART_AMOUNT_NAME = "VTLA_064";
export const CSTIC_PART_SIZING_TYPE_NAME = "VVST_064"; // Possible values: No parts, automatic split, manual split
export const CSTIC_PART_SIZING_TYPE_NONE = "N"; // Possible values: No parts, automatic split, manual split
export const CSTIC_PART_SIZING_TYPE_AUTOMATIC = "A"; // User defined amount, length automatic
export const CSTIC_PART_SIZING_TYPE_MANUAL = "J"; // User defined amount and length

export const CSTIC_PART_MIN_SIZE_NAME = "VTL01_I_064"; // Possible values: No parts, automatic split, manual split
export const CSTIC_PART_MAX_SIZE_NAME = "VTL01_A_064"; // Possible values: No parts, automatic split, manual split

export const MAX_PIECE_AMOUNT_IN_KB = 15;

const formatNumber = (num: number, digits: number) => {
  const str = "" + num;
  return str.padStart(digits, "0");
};

export const GET_GUIDEWAY_SET_IMAGE_URL = (guidewayAmount: number, index: number) =>
  `https://cdn.schaeffler-ecommerce.com/frontend/linear/linear_6_1_${guidewayAmount}_${index}.png`;
export const GET_GUIDEWAY_PIECE_SIZE_CSTIC_NAME = (index: number) => `VTL${formatNumber(index, 2)}_064`;
export const isGuidewayPieceSizeCsticName = (csticName: string) => {
  return CSTIC_GUIDEWAY_PIECE_PATTERN.test(csticName);
};
const CSTIC_GUIDEWAY_PIECE_PATTERN = /VTL\d{2}_064/;

export const getIndexFromPieceSizeCsticName = (csticName: string) => {
  const match = csticName.match(CSTIC_GUIDEWAY_PIECE_PATTERN);
  if (match) {
    const index = match[0].replace("VTL", "").replace("_064", "");
    return parseInt(index, 10);
  }
  return null;
};
export const GET_GUIDEWAY_PIECE_SIZE_SUGGESTION_LOW_CSTIC_NAME = (index: number) =>
  `VTL${formatNumber(index, 2)}_U_064`;
export const GET_GUIDEWAY_PIECE_SIZE_SUGGESTION_HIGH_CSTIC_NAME = (index: number) =>
  `VTL${formatNumber(index, 2)}_O_064`;

export const GUIDEWAY_PIECE_PATTERN = /VTL\d{2}_064/;

// Carriage kmat stuff

export const CSTIC_CARRIAGE_LOCATING_FACE = "VANS_065";

export const CSTIC_SUPPORT_DICHTUNGS_NAME = "VBUCH_065"; // Cstic only used to store Dichtungs label and info
export const CSTIC_SUPPORT_KIT_NAME = "VVSP_065"; // Cstic only used to store KIT label and info

export const CSTIC_VSKITS_NAME = "VSKITS_065";
export const CSTIC_VSKITS_DEFAULTS_VALUE = "S";
export const CSTIC_VSKITS_CUSTOM_VALUE = null;

export const CSTIC_WAGENBAUFORM_NAME = "VWAUBW_065";
export const CSTIC_SCHMIERANSCHLUSS_NAME = "VSZBW_065";
export const CSTIC_SCHMIERANSCHLUSS_POSITION_NAME = "VSARBW_065";
export const CSTIC_AUSWAHL_SCHMIERANSCHLUSS_NAME = "VSA_065";
export const CSTIC_SCHMIERANSCHLUSS_DEGEEE_NAME = "VSW_065";
export const CSTIC_DESIGN_TYPE_SUBLEVEL_NAME = "VMKBBW_065";

export const CSTIC_CARRIAGE_LEFT_NAME = "VKLIBW_065";
export const CSTIC_CARRIAGE_MIDDLE_NAME = "VKMI_065";
export const CSTIC_CARRIAGE_RIGHT_NAME = "VKREBW_065";
export const KITS_LOWER_THRESHOLD = 400;
export const KITS_UPPER_THRESHOLD = 699;
export const MOUNTED_LEFT = "L";
export const UNMOUNTED_LEFT = "BL";
export const MOUNTED_RIGHT = "R";
export const UNMOUNTED_RIGHT = "BR";
export const CARRIAGE_DESIGN_SUBKMAT_RWU = "RWU";
export const CARRIAGE_DESIGN_SUBKMAT_KWVE = "KWVE";

export const RESET_URL_RAIL_PREFIX = "railsConfiguratorPage";
export const RESET_URL_CARRIAGES_PREFIX = "carriagesConfiguratorPage";
export const RESET_URL_SHAFT_PREFIX = "shaftConfiguratorPage";
export const RESET_URL_LINEAR_PREFIX = "configuratorPage";
export const CSTIC_CARRIAGES_SMDS = "VSMDS_065";
export const CSTIC_SYSTEM_SMDS = "VSMDS_066";

export const GET_CARRIAGE_DETAILS_IMAGE_URL = (data: {
  carriageIndex: number;
  guidewayAmount: number;
  carriageAmount: number;
}) => {
  const { carriageIndex, guidewayAmount, carriageAmount } = data;
  const totalCarriageAmount = guidewayAmount * carriageAmount;
  return `https://cdn.schaeffler-ecommerce.com/frontend/linear/linear_7_3_${guidewayAmount}_${totalCarriageAmount}_${
    carriageIndex + 1
  }.png`;
};

export const DISCLAIMER_RAIL_BELOW = "DISCLAIMER_RAIL_BELOW";
export const DISCLAIMER_RAIL_ABOVE = "DISCLAIMER_RAIL_ABOVE";

export const GET_LINEAR_CONFIGURATOR_URL_FOR_ITEM = (itemId: string, importUrlPrefix: string | null) =>
  importUrlPrefix != null
    ? resolveUrl(`/${itemId}/${importUrlPrefix}/LinearConfigurator`)
    : resolveUrl(`/${itemId}/configuratorSavePage/LinearConfigurator`);

export const XLIFE_LOGO_URL = "/_ui/responsive/theme-alpha/images/xlife_logo.png";

export const getEditCarriageCsticsToReset = (configuratorType: ConfiguratorType) =>
  [
    CSTIC_CARRIAGE_LEFT_NAME,
    CSTIC_CARRIAGE_MIDDLE_NAME,
    CSTIC_CARRIAGE_RIGHT_NAME,
    CSTIC_SCHMIERANSCHLUSS_NAME,
    CSTIC_AUSWAHL_SCHMIERANSCHLUSS_NAME,
    CSTIC_SCHMIERANSCHLUSS_POSITION_NAME,
    CSTIC_SCHMIERANSCHLUSS_DEGEEE_NAME,
    CSTIC_VSKITS_NAME,
  ].filter(isNonNull);

export enum LinearPopupState {
  INITIAL,
  SENDING,
  SUCCESS,
  ERROR,
}

export const BASE_DESIGNATION_SEPARATOR = "-";

export const GET_ORDER_DESIGNTION_TEXT = (id: string) =>
  `https://cdn.schaeffler-ecommerce.com/frontend/linear/${id}_en-GB.html`;

export const ORDER_DESIGNATION_HTML_IDS = ["34233201035", "34234355211", "34234357387", "34234359563"];

export const DEFAULT_GUIDEWAYS_NUMBER_CSTIC = "V_S1_AE_066";
export const DEFAULT_GUIDEWAYS_NUMBER = "1.0";
export const DEFAULT_CARRIAGES_NUMBER_CSTIC = "VSANZ_066";
export const DEFAULT_CARRIAGES_NUMBER = "1.0";

export const FAKE_CSTIC_CONFIGURATION_VERSION = `FAKE_CONFIGURATION_VERSION`;
export const FAKE_CSTIC_UNIT_LENGTH = `FAKE_CSTIC_UNIT_LENGTH`;

export const LUBRICATION_TILE_SPECIAL_GREASING_PATTERN = "VBLT";

export const GREASING_KEY_DEFAULT_KMAT = "VBGG_066";
export const GREASING_OPTION_KEY_DEFAULT_KMAT = "VBLT_066";
export const GREASING_STANDARD_VALUE = "/";
export const GREASING_OPTION_STANDARD_VALUE = "/";

export const MANUAL_CONNECTORS_VALUE = "M";
export const MANUAL_CONNECTORS_LABEL =
  "configurator.carriage.step.configureCarriage.section.lubricationConnector.manual.name";
export const MANUAL_CONNECTORS = [
  "S02",
  "S03",
  "S04",
  "S05",
  "S12",
  "S13",
  "S14",
  "S16",
  "S25",
  "S51",
  "S54",
  "S57",
  "S62",
  "S65",
  "S75",
  "S78",
];

export const CENTRAL_CONNECTORS_VALUE = "Z";
export const CENTRAL_CONNECTORS_LABEL =
  "configurator.carriage.step.configureCarriage.section.lubricationConnector.central.name";
export const CENTRAL_CONNECTORS = [
  "S07",
  "S08",
  "S09",
  "S11",
  "S18",
  "S27",
  "S28",
  "S42",
  "S44",
  "S46",
  "S48",
  "S49",
  "S50",
  "S53",
  "S56",
  "S61",
  "S64",
  "S67",
  "S68",
  "S71",
  "S72",
  "S76",
  "S86",
  "S87",
  "S88",
];
